/**
 * Trend chart construction — UX/UI Specification §13.
 *
 * Shared by the Measurements tab and the report so a studio never sees one
 * shape on screen and a different one in the PDF.
 *
 * The axis range is computed here and frozen into the report snapshot rather
 * than recomputed at render time: otherwise a preview and a PDF generated
 * seconds apart could differ after a concurrent edit, and re-opening an old
 * report could draw it differently (§13.4).
 */

import {
  METRIC_TABLE_ORDER,
  MAX_CHARTS,
  MAX_CHART_POINTS,
  METRICS,
  MIN_CHART_POINTS,
  muscleUnitSymbol,
  type MetricKey,
} from '../constants/metrics.js';
import { formatAxisLabel, formatAxisLabelDay } from '../format/tr.js';
import {
  customMetricSeries,
  metricSeries,
  type MeasurementRecord,
  type MetricPoint,
} from './resolve.js';

export interface ChartPoint {
  measuredAt: string;
  axisLabel: string;
  value: number;
}

export interface ChartData {
  /**
   * A built-in metric key, or `custom:<folded label>` for one the studio
   * named itself. A string rather than MetricKey so a studio's own metric
   * does not need a place in a union the product ships.
   */
  metricKey: string;
  label: string;
  unit: string;
  decimals: number;
  points: ChartPoint[];
  yMin: number;
  yMax: number;
  yStep: number;
  /** Set when the chart was built from the whole history, not just the period. */
  spansAllTime?: boolean;
}

/** Steps that keep 4-5 gridlines across the ranges a human body produces. */
const STEPS: readonly number[] = [0.5, 1, 2, 5, 10, 20, 50];
const LARGEST_STEP = 50;

/**
 * Reduces a long series to at most `max` points, always keeping the first and
 * the last so the full span of the student's history survives.
 */
function sample(points: MetricPoint[], max: number): MetricPoint[] {
  if (points.length <= max) return points;

  const kept: MetricPoint[] = [points[0] as MetricPoint];
  const interior = max - 2;
  const stride = (points.length - 1) / (interior + 1);

  for (let i = 1; i <= interior; i += 1) {
    const index = Math.round(i * stride);
    const point = points[index];
    if (point && point !== kept[kept.length - 1]) kept.push(point);
  }

  kept.push(points[points.length - 1] as MetricPoint);
  return kept;
}

/**
 * A padded, rounded axis range.
 *
 * `yMin` is never forced to zero: body circumferences live in a narrow band,
 * and a zero-based axis would flatten every real change into a straight line.
 */
export function computeAxis(values: number[]): {
  yMin: number;
  yMax: number;
  yStep: number;
} {
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;

  // A flat series still needs a visible band around the line.
  const step: number =
    STEPS.find((candidate) => range === 0 || range / candidate <= 4) ?? LARGEST_STEP;

  const padding = Math.max(range * 0.15, step);
  const yMin = Math.floor((min - padding) / step) * step;
  const yMax = Math.ceil((max + padding) / step) * step;

  return { yMin, yMax, yStep: step };
}

/**
 * Which metrics get a chart: the priority order, filtered to those with enough
 * history, capped at three.
 *
 * Two points is the minimum: fewer is a dot, not a line. The connector for a
 * two-point series is drawn dashed, because the straight segment between them
 * is the one part of the chart nobody measured (§13.3).
 */
export function selectChartMetrics(
  records: readonly MeasurementRecord[],
  heightCm: number | null,
  includedKeys?: readonly MetricKey[],
): MetricKey[] {
  const allowed = includedKeys ? new Set(includedKeys) : null;
  const selected: MetricKey[] = [];

  for (const key of METRIC_TABLE_ORDER) {
    if (selected.length >= MAX_CHARTS) break;
    if (allowed && !allowed.has(key)) continue;

    if (metricSeries(records, key, heightCm).length >= MIN_CHART_POINTS) {
      selected.push(key);
    }
  }

  return selected;
}

export function buildChart(
  records: readonly MeasurementRecord[],
  key: MetricKey,
  heightCm: number | null,
): ChartData | null {
  const series = metricSeries(records, key, heightCm);
  if (series.length < MIN_CHART_POINTS) return null;

  const sampled = sample(series, MAX_CHART_POINTS);
  const definition = METRICS[key];

  const unit =
    key === 'muscle' && sampled[sampled.length - 1]?.unit
      ? muscleUnitSymbol(sampled[sampled.length - 1]?.unit ?? 'percent')
      : definition.unit;

  // Full month names up to four points, abbreviated beyond that (§5.5).
  const short = sampled.length > 4;

  // Two measurements in one month would both render as "Haziran", which reads
  // as a duplicate rather than as two separate visits. Fall back to day.month
  // for the whole series so the labels stay consistent with one another.
  const months = sampled.map((p) => p.measuredAt.slice(0, 7));
  const monthsRepeat = new Set(months).size !== months.length;

  const values = sampled.map((p) => p.value);
  const axis = computeAxis(values);

  return {
    metricKey: key,
    label: definition.label,
    unit,
    decimals: definition.decimals,
    points: sampled.map((p) => ({
      measuredAt: p.measuredAt,
      axisLabel: monthsRepeat
        ? formatAxisLabelDay(p.measuredAt)
        : formatAxisLabel(p.measuredAt, short),
      value: p.value,
    })),
    ...axis,
  };
}

/**
 * A chart for one of the studio's own metrics. Same shape, same rules; only
 * the series comes from somewhere else.
 */
export function buildCustomChart(
  records: readonly MeasurementRecord[],
  metric: { labelNormalized: string; label: string; unit: string },
): ChartData | null {
  const series = customMetricSeries(records, metric.labelNormalized);
  if (series.length < MIN_CHART_POINTS) return null;

  const sampled = sample(series, MAX_CHART_POINTS);
  const months = sampled.map((p) => p.measuredAt.slice(0, 7));
  const monthsRepeat = new Set(months).size !== months.length;
  const short = sampled.length > 4;

  return {
    metricKey: `custom:${metric.labelNormalized}`,
    label: metric.label,
    unit: metric.unit,
    decimals: 1,
    points: sampled.map((p) => ({
      measuredAt: p.measuredAt,
      axisLabel: monthsRepeat
        ? formatAxisLabelDay(p.measuredAt)
        : formatAxisLabel(p.measuredAt, short),
      value: p.value,
    })),
    ...computeAxis(sampled.map((p) => p.value)),
  };
}

/**
 * Every chart the product should draw for this student, in priority order.
 *
 * `all` is the student's whole history and is used only as a fallback: a
 * metric measured once or not at all inside the period draws nothing, and a
 * report whose physical page is a table and no picture is a poorer report than
 * one that reaches back and says it did. A chart built that way carries
 * `spansAllTime`, and the template prints that beside its title — the period
 * leak this product corrected was a *silent* one.
 */
export function buildCharts(
  records: readonly MeasurementRecord[],
  heightCm: number | null,
  includedKeys?: readonly MetricKey[],
  all?: readonly MeasurementRecord[],
): ChartData[] {
  const charts: ChartData[] = [];

  const keys = selectChartMetrics(records, heightCm, includedKeys);
  for (const key of keys) {
    const chart = buildChart(records, key, heightCm);
    if (chart) charts.push(chart);
  }

  if (!all || charts.length >= MAX_CHARTS) return charts;

  const drawn = new Set(charts.map((c) => c.metricKey));
  for (const key of selectChartMetrics(all, heightCm, includedKeys)) {
    if (charts.length >= MAX_CHARTS) break;
    if (drawn.has(key)) continue;
    const chart = buildChart(all, key, heightCm);
    if (chart) charts.push({ ...chart, spansAllTime: true });
  }

  // Table order, so the charts read in the same sequence as the rows above
  // them — not the order the two passes happened to run in.
  // Table order for the built-ins, then the studio's own after them — the
  // same sequence as the rows they sit under. -1 for a custom key sorts it
  // first under a plain indexOf, so it is pushed to the end explicitly.
  const rank = (key: string) => {
    const index = METRIC_TABLE_ORDER.indexOf(key as MetricKey);
    return index === -1 ? METRIC_TABLE_ORDER.length : index;
  };
  return charts.sort((a, b) => rank(a.metricKey) - rank(b.metricKey));
}
